/**
 * Constants for the Create Content wizard
 * Note: Icons are handled by individual components, not stored here
 * 
 * Platform Support (via Upload-Post integration):
 * - Facebook, Instagram, TikTok, Pinterest: Full support
 * - LinkedIn, YouTube, X (Twitter): Available with Upload-Post
 * - Threads: Coming soon
 */

export const platforms = [
  { id: "facebook", name: "Facebook", color: "#1877F2", icon: "facebook" },
  { id: "instagram", name: "Instagram", color: "#E4405F", icon: "instagram" },
  { id: "tiktok", name: "TikTok", color: "#000000", icon: "tiktok", requiresPerPostSettings: true },
  { id: "pinterest", name: "Pinterest", color: "#BD081C", icon: "pinterest" },
  { id: "linkedin", name: "LinkedIn", color: "#0A66C2", icon: "linkedin", comingSoon: false },
  { id: "youtube", name: "YouTube", color: "#FF0000", icon: "youtube", comingSoon: false },
  { id: "twitter", name: "X (Twitter)", color: "#000000", icon: "twitter", comingSoon: false }
];

// Platforms currently active (can post to)
export const activePlatforms = platforms.filter(p => !p.comingSoon);

// Platforms requiring special handling
export const platformsWithPerPostSettings = platforms.filter(p => p.requiresPerPostSettings).map(p => p.id);

export const languageOptions = [
  // Major Global Languages
  { id: "en", name: "English", flag: "🇬🇧" },
  { id: "es", name: "Spanish", flag: "🇪🇸" },
  { id: "zh", name: "Chinese (Mandarin)", flag: "🇨🇳" },
  { id: "hi", name: "Hindi", flag: "🇮🇳" },
  { id: "ar", name: "Arabic", flag: "🇸🇦" },
  { id: "pt", name: "Portuguese", flag: "🇧🇷" },
  { id: "fr", name: "French", flag: "🇫🇷" },
  { id: "ru", name: "Russian", flag: "🇷🇺" },
  { id: "ja", name: "Japanese", flag: "🇯🇵" },
  { id: "de", name: "German", flag: "🇩🇪" },
  // Asian Languages
  { id: "ko", name: "Korean", flag: "🇰🇷" },
  { id: "vi", name: "Vietnamese", flag: "🇻🇳" },
  { id: "th", name: "Thai", flag: "🇹🇭" },
  { id: "id", name: "Indonesian", flag: "🇮🇩" },
  { id: "ms", name: "Malay", flag: "🇲🇾" },
  { id: "tl", name: "Filipino/Tagalog", flag: "🇵🇭" },
  { id: "my", name: "Burmese", flag: "🇲🇲" },
  { id: "km", name: "Khmer", flag: "🇰🇭" },
  { id: "lo", name: "Lao", flag: "🇱🇦" },
  // South Asian Languages (Indian Subcontinent)
  { id: "bn", name: "Bengali", flag: "🇧🇩" },
  { id: "ta", name: "Tamil", flag: "🇮🇳" },
  { id: "te", name: "Telugu", flag: "🇮🇳" },
  { id: "mr", name: "Marathi", flag: "🇮🇳" },
  { id: "gu", name: "Gujarati", flag: "🇮🇳" },
  { id: "kn", name: "Kannada", flag: "🇮🇳" },
  { id: "ml", name: "Malayalam", flag: "🇮🇳" },
  { id: "pa", name: "Punjabi", flag: "🇮🇳" },
  { id: "or", name: "Odia", flag: "🇮🇳" },
  { id: "as", name: "Assamese", flag: "🇮🇳" },
  { id: "ur", name: "Urdu", flag: "🇵🇰" },
  { id: "ne", name: "Nepali", flag: "🇳🇵" },
  { id: "si", name: "Sinhala", flag: "🇱🇰" },
  // Middle Eastern Languages
  { id: "fa", name: "Persian/Farsi", flag: "🇮🇷" },
  { id: "he", name: "Hebrew", flag: "🇮🇱" },
  { id: "tr", name: "Turkish", flag: "🇹🇷" },
  // European Languages
  { id: "it", name: "Italian", flag: "🇮🇹" },
  { id: "nl", name: "Dutch", flag: "🇳🇱" },
  { id: "pl", name: "Polish", flag: "🇵🇱" },
  { id: "uk", name: "Ukrainian", flag: "🇺🇦" },
  { id: "el", name: "Greek", flag: "🇬🇷" },
  { id: "cs", name: "Czech", flag: "🇨🇿" },
  { id: "ro", name: "Romanian", flag: "🇷🇴" },
  { id: "hu", name: "Hungarian", flag: "🇭🇺" },
  { id: "sv", name: "Swedish", flag: "🇸🇪" },
  { id: "da", name: "Danish", flag: "🇩🇰" },
  { id: "no", name: "Norwegian", flag: "🇳🇴" },
  { id: "fi", name: "Finnish", flag: "🇫🇮" },
  { id: "bg", name: "Bulgarian", flag: "🇧🇬" },
  { id: "hr", name: "Croatian", flag: "🇭🇷" },
  { id: "sk", name: "Slovak", flag: "🇸🇰" },
  { id: "sl", name: "Slovenian", flag: "🇸🇮" },
  { id: "sr", name: "Serbian", flag: "🇷🇸" },
  { id: "et", name: "Estonian", flag: "🇪🇪" },
  { id: "lv", name: "Latvian", flag: "🇱🇻" },
  { id: "lt", name: "Lithuanian", flag: "🇱🇹" },
  // African Languages
  { id: "sw", name: "Swahili", flag: "🇰🇪" },
  { id: "af", name: "Afrikaans", flag: "🇿🇦" },
  { id: "am", name: "Amharic", flag: "🇪🇹" },
  // Other Languages
  { id: "ca", name: "Catalan", flag: "🇪🇸" },
  { id: "gl", name: "Galician", flag: "🇪🇸" },
  { id: "eu", name: "Basque", flag: "🇪🇸" },
  // Additional TTS-supported languages (Official OpenAI list)
  { id: "is", name: "Icelandic", flag: "🇮🇸" },
  { id: "mk", name: "Macedonian", flag: "🇲🇰" },
  { id: "kk", name: "Kazakh", flag: "🇰🇿" },
  { id: "az", name: "Azerbaijani", flag: "🇦🇿" },
  { id: "be", name: "Belarusian", flag: "🇧🇾" },
  { id: "bs", name: "Bosnian", flag: "🇧🇦" },
  { id: "hy", name: "Armenian", flag: "🇦🇲" },
  { id: "ka", name: "Georgian", flag: "🇬🇪" },
  { id: "cy", name: "Welsh", flag: "🏴󠁧󠁢󠁷󠁬󠁳󠁿" },
  { id: "mi", name: "Maori", flag: "🇳🇿" },
];

export const inputOptions = [
  { 
    id: "url", 
    title: "Paste URL", 
    subtitle: "PROMOTE ANYTHING",
    desc: "Website, blog, article, product page",
    supportedPlatforms: "Amazon • Etsy • Shopify • Gumroad • Teachable • Udemy • Kindle • App Store • Google Play • Your website"
  },
  { id: "text", title: "Type or Speak", desc: "Write your idea or use voice" },
  { id: "image", title: "Upload Image", desc: "Product photo, graphic, screenshot" },
  { id: "video", title: "Upload Video", desc: "Your video to enhance & share" }
];

export const urlIntentOptions = [
  { id: "promotional", label: "Promotional Post", desc: "AI extracts key info and creates engaging visual", icon: "🎯" },
  { id: "showcase", label: "Product Showcase", desc: "Highlight specific product/service from the page", icon: "🛍️" },
  { id: "brand", label: "Brand Awareness", desc: "General brand content inspired by the URL", icon: "✨" },
  { id: "custom", label: "Custom with Details", desc: "Add your own listing details for precise control", icon: "⚙️" },
];

export const animationOptions = [
  { id: "priceTag", label: "Price Tag", description: "Animated price slides in from right", icon: "💰" },
  { id: "lowerThird", label: "Lower Third", description: "Professional name bar at bottom", icon: "📝" },
  { id: "subtitles", label: "Subtitles", description: "Synchronized captions on video", icon: "💬" },
];

export const listingCategories = [
  { id: "property", label: "Property", icon: "🏠", fields: ["address", "price", "beds", "baths", "sqft", "mls", "features"] },
  { id: "product", label: "Product", icon: "🛍️", fields: ["name", "price", "salePrice", "features", "sku"] },
  { id: "service", label: "Service", icon: "💼", fields: ["name", "price", "duration", "features"] },
  { id: "event", label: "Event", icon: "📅", fields: ["name", "date", "time", "location", "price"] },
  { id: "job", label: "Job/Career", icon: "👔", fields: ["title", "salary", "location", "type", "benefits"] },
  { id: "restaurant", label: "Restaurant", icon: "🍽️", fields: ["dish", "price", "description", "dietary"] },
  { id: "automotive", label: "Automotive", icon: "🚗", fields: ["make", "model", "year", "price", "mileage", "condition"] },
  { id: "fitness", label: "Health/Fitness", icon: "💪", fields: ["program", "duration", "price", "results"] },
  { id: "travel", label: "Travel", icon: "✈️", fields: ["destination", "price", "duration", "rating", "includes"] },
  { id: "education", label: "Education", icon: "📚", fields: ["course", "duration", "price", "level", "instructor"] },
  { id: "pet", label: "Pet/Animal", icon: "🐾", fields: ["breed", "age", "gender", "price", "vaccinated"] },
  { id: "rental", label: "Rental", icon: "🔑", fields: ["item", "dailyRate", "weeklyRate", "deposit", "availability"] },
];

export const textPositions = [
  { id: "top-left", label: "Top Left" },
  { id: "top-center", label: "Top Center" },
  { id: "top-right", label: "Top Right" },
  { id: "center-left", label: "Center Left" },
  { id: "center", label: "Center" },
  { id: "center-right", label: "Center Right" },
  { id: "bottom-left", label: "Bottom Left" },
  { id: "bottom-center", label: "Bottom Center" },
  { id: "bottom-right", label: "Bottom Right" },
];
